<template>
  <div v-if="hasContent" class="spec-eval-panel">
    <section class="block spec-eval-health" v-if="healthBadges.length">
      <div class="spec-eval-health-head">
        <div>
          <h5>{{ healthTitle }}</h5>
          <p>{{ healthDescription }}</p>
        </div>
        <div class="spec-eval-health-badges">
          <span
            v-for="badge in healthBadges"
            :key="badge.key"
            class="spec-eval-health-badge"
            :class="'health-' + badge.tone">
            <strong>{{ badge.value }}</strong>
            {{ badge.label }}
          </span>
        </div>
      </div>
    </section>

    <section class="block spec-eval-accordion" v-if="findingRows.length" :class="{ open: findingsOpen }">
      <button class="spec-eval-summary-line" type="button" @click="findingsOpen = !findingsOpen">
        <span class="spec-eval-summary-copy">
          <span class="spec-eval-kicker">Findings</span>
          <strong>{{ findingSummary }}</strong>
          <small>{{ findingHint }}</small>
        </span>
        <span class="spec-eval-summary-badges">
          <span v-if="counts.blockers" class="count-block">{{ counts.blockers }} blocker{{ counts.blockers === 1 ? '' : 's' }}</span>
          <span v-if="counts.warnings" class="count-warn">{{ counts.warnings }} warning{{ counts.warnings === 1 ? '' : 's' }}</span>
          <span v-if="counts.estimated" class="count-est">{{ counts.estimated }} estimated</span>
          <span class="count-total">{{ findingRows.length }} total</span>
          <span class="spec-eval-chevron" :class="{ open: findingsOpen }">&rsaquo;</span>
        </span>
      </button>

      <div v-if="findingsOpen" class="spec-eval-accordion-body">
        <div class="spec-eval-toolbar">
          <h5>Finding details</h5>
          <button
            v-if="showResearchAll"
            class="spec-eval-action research"
            :disabled="isResearchBusy('all', 'all')"
            @click="$emit('research-all')">
            {{ isResearchBusy('all', 'all') ? 'Researching...' : 'AI research all' }}
          </button>
        </div>
        <div class="spec-eval-issues">
          <article
            v-for="(finding, i) in findingRows"
            :key="finding.key"
            class="spec-eval-issue"
            :class="'issue-' + finding.status">
            <span class="issue-kind">{{ findingStatusLabel(finding) }}</span>
            <div class="issue-content">
              <button
                v-if="finding.field"
                type="button"
                class="issue-field-link"
                @click.stop="$emit('scroll-field', finding.field)">
                {{ finding.field }}
              </button>
              <p>{{ finding.message }}</p>
              <div v-if="finding.current || finding.expected || finding.suggested" class="finding-facts">
                <span v-if="finding.current"><small>Current</small>{{ finding.current }}</span>
                <span v-if="finding.expected"><small>Expected</small>{{ finding.expected }}</span>
                <span v-if="finding.suggested"><small>Suggested</small>{{ finding.suggested }}</span>
              </div>
              <div
                v-if="researchStateFor(finding, i).message || researchStateFor(finding, i).suggestion"
                class="issue-research-state"
                :class="'state-' + researchStateFor(finding, i).tone">
                <strong>{{ researchStateFor(finding, i).label }}</strong>
                <span v-if="researchStateFor(finding, i).message">{{ researchStateFor(finding, i).message }}</span>
                <code v-if="researchStateFor(finding, i).suggestion">{{ researchStateFor(finding, i).suggestion }}</code>
                <div v-if="researchStateFor(finding, i).errors && researchStateFor(finding, i).errors.length" class="resolver-error-list">
                  <span v-for="(err, errIndex) in researchStateFor(finding, i).errors" :key="'err-' + errIndex">{{ err }}</span>
                </div>
                <div v-if="researchSucceeded(finding, i)" class="issue-research-actions">
                  <button class="spec-eval-action ok" @click="$emit('clear-finding', finding, i)">OK</button>
                  <button
                    class="spec-eval-action research"
                    :disabled="isResearchBusy(finding, i)"
                    @click="$emit('retry-finding', finding, i)">
                    Retry research
                  </button>
                </div>
              </div>
            </div>
            <button
              v-if="showResearch && canResearch(finding, i) && !researchSucceeded(finding, i)"
              class="spec-eval-action research"
              :disabled="isResearchBusy(finding, i)"
              @click="$emit('research-finding', finding, i)">
              {{ isResearchBusy(finding, i) ? 'Researching...' : 'AI research' }}
            </button>
          </article>
        </div>
      </div>
    </section>

    <section class="block spec-eval-accordion verify" v-if="showVerifyDiff && verifyDiffRows.length" :class="{ open: verifyOpen }">
      <button class="spec-eval-summary-line" type="button" @click="verifyOpen = !verifyOpen">
        <span class="spec-eval-summary-copy">
          <span class="spec-eval-kicker">Verify changes</span>
          <strong>{{ verifyDiffRows.length }} field{{ verifyDiffRows.length === 1 ? '' : 's' }} changed by Stage 2</strong>
          <small>{{ verifyDiffHint }}</small>
        </span>
        <span class="spec-eval-summary-badges">
          <span class="count-info">{{ verifyDiffRows.length }} changed</span>
          <span class="spec-eval-chevron" :class="{ open: verifyOpen }">&rsaquo;</span>
        </span>
      </button>

      <div v-if="verifyOpen" class="verify-diff-list">
        <article v-for="row in verifyDiffRows.slice(0, diffLimit)" :key="row.path" class="verify-diff-row" :class="{ nuked: row.setToNA }">
          <div class="diff-path">
            <span>{{ row.path }}</span>
            <strong v-if="row.setToNA">set to N/A</strong>
            <strong v-else>changed</strong>
          </div>
          <div class="diff-values">
            <span class="diff-before" :title="row.draftTitle">{{ row.draftLabel }}</span>
            <span class="diff-arrow">&rsaquo;</span>
            <span class="diff-after" :title="row.finalTitle">{{ row.finalLabel }}</span>
          </div>
          <p v-if="row.issue">{{ row.issue }}</p>
        </article>
        <small v-if="verifyDiffRows.length > diffLimit" class="diff-overflow">
          Showing the first {{ diffLimit }} verification changes.
        </small>
      </div>
    </section>

    <section class="block spec-eval-fields" v-if="showFields && fieldRows.length">
      <div class="spec-eval-fields-head">
        <div>
          <h5>Evaluated fields</h5>
          <p>Values, status, and provenance from the same evaluation model used by Candidate Review.</p>
        </div>
        <span class="count-info">{{ fieldRows.length }} fields</span>
      </div>
      <div class="spec-eval-field-grid">
        <article
          v-for="row in fieldRows"
          :key="row.path"
          class="spec-eval-field"
          :class="'field-status-' + fieldStatusClass(row.status)">
          <div class="spec-eval-field-head">
            <span>{{ row.path }}</span>
            <span class="field-status-chip" :class="'field-status-chip-' + fieldStatusClass(row.status)">
              {{ fieldStatusLabel(row.status) }}
            </span>
          </div>
          <strong :class="{ empty: row.missing }">{{ row.value || 'empty' }}</strong>
          <p v-if="row.description">{{ row.description }}</p>
          <div class="field-meta-row" v-if="fieldMetaSummary(row.provenance) || fieldMetaTierLabel(fieldMetaTier(row.provenance))">
            <span
              v-if="fieldMetaTierLabel(fieldMetaTier(row.provenance))"
              class="field-tier-badge"
              :class="'field-tier-' + fieldMetaTrustClass(row.provenance)"
              :title="fieldMetaTooltip(row.provenance)">
              {{ fieldMetaTierLabel(fieldMetaTier(row.provenance)) }}
            </span>
            <a
              v-if="fieldMetaUrl(row.provenance)"
              class="field-meta-link"
              :href="fieldMetaUrl(row.provenance)"
              target="_blank"
              rel="noopener noreferrer">
              source
            </a>
            <span v-if="fieldMetaSummary(row.provenance)" class="field-meta-summary" :title="fieldMetaTooltip(row.provenance)">
              {{ fieldMetaSummary(row.provenance) }}
            </span>
          </div>
        </article>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'SpecEvaluationPanel',
  props: {
    item: { type: Object, default: null },
    healthTitle: { type: String, default: 'Evaluation health' },
    healthDescription: { type: String, default: 'Typed evaluation from catalog rules, AI research, and source provenance.' },
    showFields: { type: Boolean, default: false },
    showResearch: { type: Boolean, default: false },
    showResearchAll: { type: Boolean, default: false },
    showVerifyDiff: { type: Boolean, default: true },
    diffLimit: { type: Number, default: 12 },
    researchState: { type: Function, default: function () { return {} } },
    researchSucceeded: { type: Function, default: function () { return false } },
    canResearch: { type: Function, default: function () { return false } },
    isResearchBusy: { type: Function, default: function () { return false } }
  },
  data () {
    return {
      findingsOpen: false,
      verifyOpen: false
    }
  },
  computed: {
    evaluation () {
      return this.evaluationFor(this.item)
    },
    fieldRows () {
      return this.evaluationFields(this.item)
    },
    findingRows () {
      return this.evaluationProblems(this.item)
    },
    counts () {
      return this.evaluationCounts(this.item)
    },
    healthBadges () {
      if (!this.evaluation) return []
      const counts = this.counts
      return [
        { key: 'blockers', label: 'blockers', value: counts.blockers, tone: 'blocker' },
        { key: 'warnings', label: 'warnings', value: counts.warnings, tone: 'warning' },
        { key: 'estimated', label: 'estimated', value: counts.estimated, tone: 'estimated' },
        { key: 'ok', label: 'ok', value: counts.ok, tone: 'ok' }
      ].filter(function (badge) {
        return badge.value !== undefined && badge.value !== null
      })
    },
    verifyDiffRows () {
      return this.verifyRowsFor(this.item)
    },
    hasContent () {
      return !!(this.healthBadges.length || this.findingRows.length || this.verifyDiffRows.length || (this.showFields && this.fieldRows.length))
    },
    findingSummary () {
      if (!this.findingRows.length) return 'No current findings'
      const parts = []
      if (this.counts.blockers) parts.push(`${this.counts.blockers} blocker${this.counts.blockers === 1 ? '' : 's'}`)
      if (this.counts.warnings) parts.push(`${this.counts.warnings} warning${this.counts.warnings === 1 ? '' : 's'}`)
      if (!parts.length) parts.push(`${this.findingRows.length} finding${this.findingRows.length === 1 ? '' : 's'}`)
      return parts.join(' / ')
    },
    findingHint () {
      const first = this.findingRows[0]
      if (!first) return 'No typed evaluation problems.'
      return this.truncateText(first.message, 132)
    },
    verifyDiffHint () {
      const row = this.verifyDiffRows[0]
      if (!row) return 'No verify-pass changes to show.'
      return `${row.path}: ${row.draftLabel} to ${row.finalLabel}`
    }
  },
  methods: {
    evaluationFor (item) {
      if (!item) return null
      const finalSpec = item.final_spec || item.finalSpec || {}
      return item.evaluation || item.evaluation_data || item.evaluationData ||
        item.spec_evaluation || item.specEvaluation ||
        finalSpec.evaluation || finalSpec.evaluation_data || finalSpec.evaluationData ||
        finalSpec.spec_evaluation || finalSpec.specEvaluation || null
    },
    normalizeEvaluationGroup (group) {
      const text = String(group || '').trim().toLowerCase()
      if (text === 'compat' || text === 'compatibility' || text === 'compatible' || text === 'compats') return 'compats'
      if (text === 'spec' || text === 'specification' || text === 'specifications' || text === 'specs') return 'specs'
      if (text === 'general' || text === 'catalog' || text === 'base') return 'general'
      return text
    },
    normalizeEvaluationPath (field, fallbackPath, fallbackGroup, fallbackKey) {
      const rawPath = field && (field.path || field.field || field.field_path || field.fieldPath || field.key)
      let path = String(rawPath || fallbackPath || '').trim()
      let group = this.normalizeEvaluationGroup(field && (field.group || field.section || field.category || field.scope || fallbackGroup))
      let key = String((field && (field.name || field.field_name || field.fieldName)) || fallbackKey || '').trim()

      if (path && path.indexOf('.') >= 0) {
        const parts = path.split('.')
        group = this.normalizeEvaluationGroup(parts.shift())
        key = parts.join('.')
      } else if (path && !key) {
        key = path
      }

      if (!group && fallbackGroup) group = this.normalizeEvaluationGroup(fallbackGroup)
      if (!group || !key || ['general', 'specs', 'compats'].indexOf(group) < 0) return null
      return { group, key, path: `${group}.${key}` }
    },
    normalizeEvaluationStatus (value, message) {
      const text = String(value || '').trim().toLowerCase()
      const msg = String(message || '').trim().toLowerCase()
      if (/block|error|required|invalid|missing/.test(text)) return 'blocker'
      if (/warn|unverified|could_not|could not|needs/.test(text)) return 'warning'
      if (/estimate|estimated|rule|derived/.test(text)) return 'estimated'
      if (/source|note|info/.test(text)) return 'note'
      if (/ok|valid|verified|clean|pass|success|complete/.test(text)) return 'ok'
      if (/validation blocker|required field|must be populated|missing_required|invalid/.test(msg)) return 'blocker'
      if (/could not verify|could not|missing|failed|unavailable/.test(msg)) return 'warning'
      if (/research source|official research filled|research complete|research updated|source:/.test(msg)) return 'note'
      return text || 'ok'
    },
    evaluationFieldValue (field) {
      if (!field || typeof field !== 'object') return ''
      const keys = ['value', 'finalValue', 'final_value', 'current', 'currentValue', 'current_value', 'resolvedValue', 'resolved_value']
      for (const key of keys) {
        if (field[key] !== undefined && field[key] !== null) return field[key]
      }
      return ''
    },
    normalizeEvaluationProvenance (field) {
      if (!field || typeof field !== 'object') return null
      const provenance = field.provenance || field.source_provenance || field.sourceProvenance || field.field_meta || field.fieldMeta || field.meta || field.metadata
      if (provenance && typeof provenance === 'object') return provenance
      const hasInline = field.tier !== undefined || field.source || field.url || field.confidence !== undefined || field.provider || field.model
      if (!hasInline) return null
      return {
        tier: field.tier,
        source: field.source,
        url: field.url,
        confidence: field.confidence,
        provider: field.provider,
        model: field.model
      }
    },
    normalizeEvaluationField (field, fallbackPath, fallbackGroup, fallbackKey) {
      if (!field || typeof field !== 'object') field = { value: field }
      const pathInfo = this.normalizeEvaluationPath(field, fallbackPath, fallbackGroup, fallbackKey)
      if (!pathInfo) return null
      const value = this.evaluationFieldValue(field)
      const status = this.normalizeEvaluationStatus(field.status || field.severity || field.state || field.level || field.kind, field.message || field.reason || '')
      return Object.assign({}, pathInfo, {
        value: this.formatValue(value),
        status,
        missing: field.missing === true || this.isMissingValue(value),
        description: field.description || field.tooltip || field.help || field.reason || field.message || '',
        expected: this.formatValue(field.expected || field.expected_value || field.expectedValue || ''),
        suggested: this.formatValue(field.suggested || field.suggested_value || field.suggestedValue || ''),
        provenance: this.normalizeEvaluationProvenance(field)
      })
    },
    evaluationFields (item) {
      const evaluation = this.evaluationFor(item)
      const fields = evaluation && evaluation.fields
      if (!fields) return []
      const rows = []
      const push = (field, path, group, key) => {
        const row = this.normalizeEvaluationField(field, path, group, key)
        if (row) rows.push(row)
      }

      if (Array.isArray(fields)) {
        fields.forEach((field, index) => push(field, '', '', String(index)))
      } else if (typeof fields === 'object') {
        Object.keys(fields).forEach((groupOrPath) => {
          const value = fields[groupOrPath]
          const normalizedGroup = this.normalizeEvaluationGroup(groupOrPath)
          if (['general', 'specs', 'compats'].indexOf(normalizedGroup) >= 0 && Array.isArray(value)) {
            value.forEach((field, index) => push(field, '', normalizedGroup, field && (field.key || field.name || field.field || index)))
          } else if (['general', 'specs', 'compats'].indexOf(normalizedGroup) >= 0 && value && typeof value === 'object') {
            Object.keys(value).forEach((key) => push(value[key], `${normalizedGroup}.${key}`, normalizedGroup, key))
          } else {
            push(value, groupOrPath, '', '')
          }
        })
      }

      const seen = {}
      return rows.filter((row) => {
        const key = row.path.toLowerCase()
        if (seen[key]) return false
        seen[key] = true
        return true
      })
    },
    evaluationProblemSource (item) {
      const evaluation = this.evaluationFor(item)
      return evaluation && (evaluation.problems || evaluation.findings || evaluation.issues) || null
    },
    normalizeEvaluationProblem (problem, fallbackPath, index) {
      if (!problem || typeof problem !== 'object') problem = { message: this.formatValue(problem) }
      const path = this.normalizeIssueFieldPath(problem.field || problem.path || problem.field_path || problem.fieldPath || fallbackPath || '')
      const message = problem.message || problem.description || problem.reason || this.formatValue(problem)
      const status = this.normalizeEvaluationStatus(problem.status || problem.severity || problem.state || problem.level || problem.kind, message)
      return {
        key: (problem.id || problem.issueId || problem.issue_id || path || 'problem') + ':' + index,
        id: problem.id || problem.issueId || problem.issue_id || null,
        field: path,
        status,
        message,
        current: this.formatValue(problem.current || problem.current_value || problem.currentValue || problem.value || ''),
        expected: this.formatValue(problem.expected || problem.expected_value || problem.expectedValue || ''),
        suggested: this.formatValue(problem.suggested || problem.suggested_value || problem.suggestedValue || ''),
        raw: Object.assign({}, problem, { field: path, message })
      }
    },
    evaluationProblems (item) {
      const problems = this.evaluationProblemSource(item)
      if (!problems) return []
      const rows = []
      if (Array.isArray(problems)) {
        problems.forEach((problem, index) => rows.push(this.normalizeEvaluationProblem(problem, '', index)))
      } else if (typeof problems === 'object') {
        Object.keys(problems).forEach((path, index) => rows.push(this.normalizeEvaluationProblem(problems[path], path, index)))
      }
      return rows.filter((row) => row && this.isActionableFinding(row))
    },
    isActionableFinding (row) {
      if (!row || !row.message) return false
      const message = String(row.message || '').toLowerCase()
      if (/research source|official research filled|research complete|research updated|source:/.test(message)) return false
      if (row.status === 'blocker' || row.status === 'warning') return true
      if (/validation blocker|required field|must be populated|missing_required|could not verify|could not|failed|invalid/.test(message)) return true
      return false
    },
    evaluationCounts (item) {
      const evaluation = this.evaluationFor(item)
      const raw = evaluation && evaluation.counts && typeof evaluation.counts === 'object' ? evaluation.counts : {}
      const problems = this.evaluationProblems(item)
      const valueFor = (...keys) => {
        for (const key of keys) {
          if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') return Number(raw[key]) || 0
        }
        return null
      }
      const counts = {
        total: problems.length,
        blockers: valueFor('blockers', 'blocker'),
        warnings: valueFor('warnings', 'warning'),
        estimated: valueFor('estimated', 'estimates'),
        ok: valueFor('ok', 'valid', 'verified')
      }
      if (counts.blockers === null) counts.blockers = problems.filter((row) => row.status === 'blocker').length
      if (counts.warnings === null) counts.warnings = problems.filter((row) => row.status === 'warning').length
      if (counts.estimated === null) counts.estimated = this.evaluationFields(item).filter((row) => row.status === 'estimated').length
      if (counts.ok === null) counts.ok = this.evaluationFields(item).filter((row) => row.status === 'ok').length
      return counts
    },
    fieldStatusClass (status) {
      const normalized = this.normalizeEvaluationStatus(status)
      if (['blocker', 'warning', 'estimated', 'ok'].indexOf(normalized) >= 0) return normalized
      return 'ok'
    },
    fieldStatusLabel (status) {
      const map = { blocker: 'Blocker', warning: 'Warning', estimated: 'Estimated', ok: 'OK' }
      return map[this.fieldStatusClass(status)] || this.formatValue(status)
    },
    findingStatusLabel (finding) {
      return this.fieldStatusLabel(finding && finding.status)
    },
    researchStateFor (finding, index) {
      const state = this.researchState(finding, index)
      return state || {}
    },
    formatValue (value) {
      if (value === null || value === undefined) return ''
      if (typeof value === 'object') {
        try { return JSON.stringify(value) } catch (e) { return String(value) }
      }
      return String(value)
    },
    isMissingValue (value) {
      if (value === null || value === undefined) return true
      const text = String(value).trim()
      return !text || /^(n\/a|na|null|undefined|none|-|--|unknown)$/i.test(text)
    },
    truncateText (value, max) {
      const text = String(value || '').replace(/\s+/g, ' ').trim()
      if (text.length <= max) return text
      return text.slice(0, Math.max(0, max - 1)).trim() + '...'
    },
    normalizeIssueFieldPath (path) {
      const text = String(path || '').trim()
      if (!text) return ''
      return text.replace(/^compat\./i, 'compats.')
    },
    specCompareValue (value) {
      return this.formatValue(value).trim()
    },
    flattenSpecValues (spec) {
      const rows = {}
      const source = spec && typeof spec === 'object' ? spec : {}
      ;['general', 'specs', 'compats', 'compat'].forEach((group) => {
        const normalizedGroup = group === 'compat' ? 'compats' : group
        const values = source[group] && typeof source[group] === 'object' ? source[group] : {}
        Object.keys(values).forEach((key) => {
          rows[`${normalizedGroup}.${key}`] = this.specCompareValue(values[key])
        })
      })
      return rows
    },
    verifyRowsFor (item) {
      if (!item || !item.draft_spec || !item.final_spec) return []
      const draft = this.flattenSpecValues(item.draft_spec)
      const final = this.flattenSpecValues(item.final_spec)
      const keys = Object.keys(Object.assign({}, draft, final)).sort((a, b) => a.localeCompare(b))
      return keys.map((path) => {
        const draftValue = draft[path] === undefined ? '' : draft[path]
        const finalValue = final[path] === undefined ? '' : final[path]
        if (draftValue === finalValue) return null
        return {
          path,
          draftLabel: this.truncateText(draftValue || 'empty', 70),
          finalLabel: this.truncateText(finalValue || 'empty', 70),
          draftTitle: draftValue || 'empty',
          finalTitle: finalValue || 'empty',
          setToNA: !this.isMissingValue(draftValue) && this.isMissingValue(finalValue),
          issue: this.verifyIssueForPath(item, path)
        }
      }).filter(Boolean)
    },
    verifyIssueForPath (item, path) {
      const normalizedPath = this.normalizeIssueFieldPath(path)
      const issues = (item && item.issues) || []
      const exact = issues.find((issue) => this.normalizeIssueFieldPath(this.issueFieldPath(issue)) === normalizedPath)
      if (exact) return this.issueMessage(exact)
      const leaf = normalizedPath.split('.').pop()
      const loose = issues.find((issue) => {
        const text = this.issueMessage(issue).toLowerCase()
        return text.indexOf(normalizedPath.toLowerCase()) >= 0 || (leaf && text.indexOf(String(leaf).toLowerCase()) >= 0)
      })
      return loose ? this.issueMessage(loose) : ''
    },
    issueMessage (issue) {
      if (typeof issue === 'string') return issue
      if (issue && typeof issue === 'object') return issue.message || issue.reason || issue.field || JSON.stringify(issue)
      return String(issue || '')
    },
    issueFieldPath (issue) {
      if (!issue) return ''
      if (typeof issue === 'object') {
        const value = issue.field || issue.path || issue.key || issue.field_path || issue.fieldPath
        if (value) return String(value)
      }
      const text = this.issueMessage(issue)
      const match = text.match(/\b(?:general|specs|compats|compat|price)\.[A-Za-z0-9_]+/i)
      return match ? match[0] : ''
    },
    fieldMetaTier (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const keys = ['tier', 'source_tier', 'sourceTier', 'evidence_tier', 'evidenceTier', 'confidence_tier', 'confidenceTier']
      for (const key of keys) {
        if (meta[key] !== undefined && meta[key] !== null && meta[key] !== '') return meta[key]
      }
      return ''
    },
    fieldMetaTierLabel (tier) {
      const text = String(tier || '').trim()
      if (!text && tier !== 0) return ''
      const number = Number(text)
      if (!isNaN(number)) {
        if (number === 0) return 'T0 rule'
        if (number === 1) return 'T1 DeepSeek'
        if (number === 2) return 'T2 web-source'
        return `T${number}`
      }
      const lowered = text.toLowerCase()
      if (/^(t0|0|rule|tier 0|tier_0|tier-0)/.test(lowered)) return 'T0 rule'
      if (/^(t1|1|deepseek|ai|tier 1|tier_1|tier-1)/.test(lowered)) return 'T1 DeepSeek'
      if (/^(t2|2|web|web-source|source|tier 2|tier_2|tier-2)/.test(lowered)) return 'T2 web-source'
      return text.replace(/^tier[_\s-]*/i, 'T').replace(/_/g, ' ').slice(0, 14)
    },
    fieldMetaSource (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const source = meta.source || meta.source_name || meta.sourceName || meta.source_title || meta.sourceTitle || meta.origin || meta.from
      if (source) {
        if (typeof source === 'object') return source.name || source.title || source.url || ''
        return String(source)
      }
      const provider = meta.provider || meta.ai_provider || meta.aiProvider || meta.model_provider || meta.modelProvider
      const model = meta.model || meta.ai_model || meta.aiModel
      if (provider || model) return [this.prettyProvider(provider), model].filter(Boolean).join(' / ')
      const url = this.fieldMetaUrl(meta)
      if (url) return this.hostnameFromUrl(url)
      return ''
    },
    fieldMetaConfidence (meta) {
      if (!meta || typeof meta !== 'object') return ''
      const value = meta.confidence !== undefined ? meta.confidence
        : (meta.score !== undefined ? meta.score
          : (meta.confidence_score !== undefined ? meta.confidence_score : meta.confidenceScore))
      if (value === undefined || value === null || value === '') return ''
      const number = Number(value)
      if (!isNaN(number)) {
        const pct = number <= 1 ? Math.round(number * 100) : Math.round(number)
        return `${pct}%`
      }
      return String(value)
    },
    fieldMetaTrustClass (meta) {
      const value = this.fieldMetaConfidence(meta).toLowerCase()
      if (/high|strong|trusted|verified|^9\d%$|^100%$/.test(value)) return 'high'
      if (/medium|moderate|mid|^([5-8]\d)%$/.test(value)) return 'mid'
      if (/low|weak|guess|^([0-4]\d)%$/.test(value)) return 'low'
      return 'neutral'
    },
    fieldMetaUrl (meta) {
      if (!meta || typeof meta !== 'object') return ''
      return meta.url || meta.source_url || meta.sourceUrl || meta.href || ''
    },
    fieldMetaSummary (meta) {
      if (!meta) return ''
      return [this.fieldMetaSource(meta), this.fieldMetaConfidence(meta)].filter(Boolean).join(' / ')
    },
    fieldMetaTooltip (meta) {
      if (!meta) return ''
      const rows = []
      const source = this.fieldMetaSource(meta)
      const confidence = this.fieldMetaConfidence(meta)
      const tier = this.fieldMetaTier(meta)
      const url = this.fieldMetaUrl(meta)
      const note = meta.note || meta.reason || meta.evidence || meta.summary
      if (source) rows.push(`Source: ${source}`)
      if (confidence) rows.push(`Confidence: ${confidence}`)
      if (tier || tier === 0) rows.push(`Tier: ${this.fieldMetaTierLabel(tier)}`)
      if (url) rows.push(`URL: ${url}`)
      if (note) rows.push(`Note: ${note}`)
      return rows.join('\n')
    },
    prettyProvider (provider) {
      const text = String(provider || '').trim()
      const map = { openai: 'OpenAI', deepseek: 'DeepSeek', web_search: 'Web search' }
      return map[text.toLowerCase()] || text
    },
    hostnameFromUrl (url) {
      try {
        return new URL(String(url || '')).hostname.replace(/^www\./, '')
      } catch (e) {
        return String(url || '')
      }
    }
  }
}
</script>

<style scoped>
.spec-eval-panel {
  display: grid;
  gap: 14px;
}

.block {
  background: rgba(7, 11, 18, 0.48);
  border: 1px solid rgba(80, 91, 116, 0.74);
  border-radius: 8px;
  padding: 16px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.spec-eval-health-head,
.spec-eval-fields-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

h5,
p {
  margin: 0;
}

h5 {
  color: #20c8f8;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

p,
small {
  color: #9faabe;
}

.spec-eval-health-head p,
.spec-eval-fields-head p {
  margin-top: 6px;
  font-size: 13px;
}

.spec-eval-health-badges,
.spec-eval-summary-badges {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.spec-eval-health-badge,
.count-block,
.count-warn,
.count-est,
.count-total,
.count-info,
.field-status-chip,
.field-tier-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 22px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  line-height: 1;
}

.spec-eval-health-badge {
  color: #d6deec;
  background: rgba(53, 61, 79, 0.72);
  border: 1px solid rgba(91, 105, 134, 0.66);
}

.spec-eval-health-badge strong {
  color: #f0f5ff;
}

.health-blocker,
.count-block,
.issue-blocker .issue-kind,
.field-status-chip-blocker {
  color: #ff8ea8;
  background: rgba(118, 31, 54, 0.52);
  border: 1px solid rgba(255, 83, 123, 0.56);
}

.health-warning,
.count-warn,
.issue-warning .issue-kind,
.field-status-chip-warning {
  color: #f3cc75;
  background: rgba(103, 78, 26, 0.42);
  border: 1px solid rgba(219, 179, 80, 0.54);
}

.health-estimated,
.count-est,
.field-status-chip-estimated {
  color: #76cdf4;
  background: rgba(20, 81, 111, 0.42);
  border: 1px solid rgba(47, 176, 225, 0.5);
}

.health-ok,
.field-status-chip-ok {
  color: #7ce0bf;
  background: rgba(10, 97, 74, 0.34);
  border: 1px solid rgba(28, 188, 140, 0.46);
}

.count-total,
.count-info {
  color: #aeb8ca;
  background: rgba(34, 41, 56, 0.72);
  border: 1px solid rgba(78, 90, 116, 0.68);
}

.spec-eval-summary-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 16px;
  margin: 0;
  padding: 0;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.spec-eval-summary-copy {
  min-width: 0;
}

.spec-eval-kicker {
  display: block;
  margin-bottom: 5px;
  color: #20c8f8;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.spec-eval-summary-copy strong {
  display: block;
  color: #e0e6f2;
  font-size: 16px;
  line-height: 1.3;
}

.spec-eval-summary-copy small {
  display: block;
  margin-top: 4px;
}

.spec-eval-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: #9faabe;
  border: 1px solid rgba(95, 108, 136, 0.7);
  border-radius: 7px;
  transform: rotate(90deg);
  transition: transform 0.16s ease, color 0.16s ease, border-color 0.16s ease;
}

.spec-eval-chevron.open {
  color: #20c8f8;
  border-color: rgba(32, 200, 248, 0.56);
  transform: rotate(-90deg);
}

.spec-eval-accordion-body,
.verify-diff-list {
  margin-top: 14px;
}

.spec-eval-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.spec-eval-issues,
.verify-diff-list {
  display: grid;
  gap: 10px;
}

.spec-eval-issue,
.verify-diff-row,
.spec-eval-field {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: flex-start;
  padding: 12px;
  border: 1px solid rgba(72, 84, 110, 0.76);
  border-radius: 8px;
  background: rgba(8, 12, 20, 0.58);
}

.issue-kind {
  padding: 5px 11px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
}

.issue-content {
  min-width: 0;
}

.issue-content p {
  color: #dbe2ee;
  font-size: 13px;
  line-height: 1.45;
}

.issue-field-link {
  margin: 0 0 6px;
  padding: 3px 8px;
  color: #b9c4d6;
  font-family: Consolas, "SFMono-Regular", monospace;
  font-size: 12px;
  background: rgba(55, 63, 82, 0.64);
  border: 1px solid rgba(90, 103, 132, 0.56);
  border-radius: 6px;
  cursor: pointer;
}

.finding-facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.finding-facts span {
  display: grid;
  gap: 3px;
  min-width: 120px;
  padding: 7px 9px;
  color: #dfe5ef;
  background: rgba(12, 18, 28, 0.7);
  border: 1px solid rgba(70, 83, 108, 0.72);
  border-radius: 7px;
}

.finding-facts small {
  color: #8793a8;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
}

.spec-eval-action {
  min-height: 28px;
  padding: 0 11px;
  color: #c9d4e6;
  font-size: 12px;
  font-weight: 900;
  border: 1px solid rgba(92, 107, 138, 0.78);
  border-radius: 6px;
  background: rgba(19, 25, 37, 0.74);
}

.spec-eval-action.research {
  color: #b7ecff;
  border-color: rgba(32, 200, 248, 0.46);
}

.spec-eval-action.ok {
  color: #82e6c8;
  border-color: rgba(30, 190, 141, 0.48);
}

.spec-eval-action:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.issue-research-state {
  display: grid;
  gap: 4px;
  margin-top: 9px;
  padding: 10px 11px;
  border-radius: 7px;
  font-size: 12px;
}

.issue-research-state.state-good {
  color: #bdf4e2;
  background: rgba(8, 65, 50, 0.36);
  border: 1px solid rgba(29, 188, 141, 0.5);
}

.issue-research-state.state-warn {
  color: #f1d08a;
  background: rgba(84, 62, 22, 0.36);
  border: 1px solid rgba(219, 179, 80, 0.48);
}

.issue-research-state.state-bad {
  color: #ffb0bf;
  background: rgba(88, 26, 43, 0.38);
  border: 1px solid rgba(255, 83, 123, 0.48);
}

.issue-research-actions {
  display: flex;
  gap: 8px;
  margin-top: 5px;
}

.resolver-error-list {
  display: grid;
  gap: 3px;
}

.resolver-error-list span {
  color: #ffb0bf;
}

.verify-diff-row {
  grid-template-columns: minmax(150px, 260px) minmax(0, 1fr);
}

.diff-path {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.diff-path span {
  color: #d6deec;
  font-weight: 800;
}

.diff-path strong {
  padding: 3px 7px;
  color: #b8c2d3;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  background: rgba(64, 73, 94, 0.76);
  border-radius: 999px;
}

.verify-diff-row.nuked .diff-path strong {
  color: #f3cc75;
  background: rgba(103, 78, 26, 0.42);
}

.diff-values {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 10px;
  align-items: center;
}

.diff-before,
.diff-after {
  min-width: 0;
  overflow: hidden;
  padding: 8px 10px;
  color: #cfd6e4;
  white-space: nowrap;
  text-overflow: ellipsis;
  border: 1px solid rgba(74, 86, 112, 0.8);
  border-radius: 7px;
  background: rgba(8, 12, 20, 0.7);
}

.diff-after {
  border-color: rgba(32, 200, 248, 0.42);
}

.diff-arrow {
  color: #20c8f8;
  font-weight: 900;
}

.verify-diff-row p {
  grid-column: 2;
  color: #98a5b8;
  font-size: 12px;
}

.diff-overflow {
  display: block;
  margin-top: 4px;
}

.spec-eval-field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.spec-eval-field {
  grid-template-columns: minmax(0, 1fr);
}

.spec-eval-field-head,
.field-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}

.spec-eval-field-head span:first-child {
  color: #9faabe;
  font-size: 12px;
  font-weight: 900;
}

.spec-eval-field strong {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: #e3e9f4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.spec-eval-field strong.empty {
  color: #f3cc75;
}

.spec-eval-field p {
  color: #93a0b5;
  font-size: 12px;
  line-height: 1.45;
}

.field-tier-high,
.field-tier-mid,
.field-tier-low,
.field-tier-neutral {
  color: #cfd8e8;
  background: rgba(40, 48, 65, 0.72);
  border: 1px solid rgba(83, 96, 125, 0.66);
}

.field-tier-high {
  color: #7ce0bf;
  border-color: rgba(30, 190, 141, 0.46);
}

.field-tier-mid {
  color: #76cdf4;
  border-color: rgba(47, 176, 225, 0.5);
}

.field-tier-low {
  color: #f3cc75;
  border-color: rgba(219, 179, 80, 0.54);
}

.field-meta-link {
  color: #20c8f8;
  font-size: 12px;
  font-weight: 900;
  text-decoration: none;
}

.field-meta-summary {
  color: #8f9cb1;
  font-size: 12px;
}

@media screen and (max-width: 720px) {
  .spec-eval-health-head,
  .spec-eval-summary-line,
  .spec-eval-fields-head {
    align-items: stretch;
    flex-direction: column;
  }

  .spec-eval-health-badges,
  .spec-eval-summary-badges {
    justify-content: flex-start;
  }

  .spec-eval-issue,
  .verify-diff-row {
    grid-template-columns: 1fr;
  }

  .verify-diff-row p {
    grid-column: auto;
  }

  .diff-values {
    grid-template-columns: 1fr;
  }

  .diff-arrow {
    display: none;
  }
}
</style>
